import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, authKeys } from '@watchparty/shared';
const AuthContext = createContext(null);
export function AuthProvider({ children }) {
    const queryClient = useQueryClient();
    const { data: user, isLoading } = useQuery({
        queryKey: authKeys.me(),
        queryFn: authApi.getMe,
        retry: false,
        staleTime: 1000 * 60 * 5,
    });
    const login = useCallback(async (credentials) => {
        await authApi.login(credentials);
        await queryClient.invalidateQueries({ queryKey: authKeys.me() });
    }, [queryClient]);
    const register = useCallback(async (credentials) => {
        const response = await authApi.register(credentials);
        if (!response.emailVerificationRequired) {
            await queryClient.invalidateQueries({ queryKey: authKeys.me() });
        }
        return response;
    }, [queryClient]);
    const logout = useCallback(async () => {
        await authApi.logout();
        queryClient.setQueryData(authKeys.me(), null);
        queryClient.clear();
    }, [queryClient]);
    const refreshUser = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: authKeys.me() });
    }, [queryClient]);
    const value = {
        user: user ?? null,
        isAuthenticated: !!user,
        isAdmin: user?.isAdmin ?? false,
        onboardingComplete: user?.onboardingComplete ?? false,
        isLoading,
        login,
        register,
        logout,
        refreshUser,
    };
    return _jsx(AuthContext, { value: value, children: children });
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
